import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCountry } from "../context/CountryContext";
import {
  Home, User, CarTaxiFront, Package, UserPlus, Users, Globe, LogIn, Menu, X, Info, AlertTriangle, LifeBuoy, Shield, Mail, Banknote,
} from "lucide-react";
import { Bike, Car } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ContactModal } from "./ContactModal";
import { AdminZoneSwitch } from "./AdminZoneSwitch";
import { useZone } from "../context/ZoneContext";
import { useAuth } from "../context/AuthContext";

const Logo = ({ onClick }) => {
  const { zone } = useZone();
  const isEu = zone === "europe";
  const isNold = typeof window !== "undefined" && /noldgo\./i.test(window.location.hostname);
  return (
    <button data-testid="brand-logo" onClick={onClick} className="flex items-center gap-2 group">
      {isNold ? (
        <img src="/nold-icon.jpg?v=7" alt="Nold Go" className="w-11 h-11 rounded-xl shrink-0 shadow-md object-cover bg-white" />
      ) : isEu ? (
        <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] flex items-center justify-center shrink-0 shadow-md">
          <Car className="w-6 h-6 text-white" strokeWidth={2.4} />
        </span>
      ) : (
        <img src="/brand-logo.png" alt="BentSkin Go" className="w-11 h-11 rounded-xl shrink-0 shadow-md object-cover" />
      )}
      <span className="font-head font-extrabold text-2xl tracking-tight text-white">
        {isEu ? "Nold" : "BentSkin"}<span className="text-[#F26419]"> Go</span>
      </span>
    </button>
  );
};

const LANG_LABELS = { fr: "Français", en: "English", de: "Deutsch", es: "Español" };

export function SideNav() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { active: countries, currentCountry, setCountry } = useCountry();
  const { zone } = useZone();
  const { user } = useAuth();
  const isEu = zone === "europe";
  const isAdmin = !!(user && (user.role === "admin" || user.is_super_admin));
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("accueil");
  const [contactOpen, setContactOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const _isNold = typeof window !== "undefined" && /noldgo\./i.test(window.location.hostname);
  const _langs = (!_isNold && typeof localStorage !== "undefined" && localStorage.getItem("bsg_zone") === "africa") ? ["fr", "en"] : ["fr", "en", "de", "es"];
  const _cur = (i18n.language || "fr").slice(0, 2);
  const lang = (_langs.includes(_cur) ? _cur : "fr").toUpperCase();

  const scrollTo = (id) => {
    if (id === "top") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const mainItems = [
    { key: "accueil", label: t("nav.home"), icon: Home, run: () => scrollTo("top") },
    { key: "passenger", label: "Commander", icon: User, run: () => navigate("/auth?role=passenger") },
    { key: "driver", label: "Travailler avec nous", icon: CarTaxiFront, run: () => navigate("/auth?role=driver") },
    { key: "colis", label: isEu ? "🍔 Livraison Repas" : "Colis", icon: isEu ? Bike : Package, run: () => {
        // Intent PORTÉE PAR L'URL (?intent=delivery) → PassengerDashboard force le mode "velo" (Europe) ou "delivery" (Afrique).
        // Fallback localStorage conservé pour les navigateurs qui perdent les query params.
        localStorage.setItem("bsg_intent", "delivery");
        if (user) navigate("/passenger?intent=delivery");
        else navigate("/auth?role=passenger&intent=delivery");
      } },
  ];
  const items = mainItems;

  const secondaryItems = [
    { key: "about", label: t("nav.about"), icon: Info, run: () => scrollTo("about") },
    { key: "complaints", label: t("nav.complaints"), icon: AlertTriangle, run: () => scrollTo("complaints") },
    { key: "conditions", label: "Conditions & confidentialité", icon: Shield, run: () => navigate("/confidentialite") },
    { key: "support", label: t("nav.support"), icon: LifeBuoy, run: () => setContactOpen(true) },
    ...(!isEu ? [
      { key: "agent", label: t("becomeAgent.cta"), icon: UserPlus, run: () => scrollTo("become-agent") },
      { key: "team", label: t("agent.space"), icon: Users, run: () => navigate("/equipe") },
    ] : []),
    ...(isAdmin ? [
      { key: "direction", label: "Espace Direction", icon: Mail, run: () => navigate("/admin") },
      { key: "admin", label: "Admin", icon: Shield, run: () => navigate("/admin") },
    ] : []),
  ];

  const handle = (it) => { setCurrent(it.key); setOpen(false); it.run(); };

  const NavList = () => (
    <nav className="flex flex-col gap-1.5 px-4 mt-2">
      {items.map((it) => {
        const activeItem = current === it.key;
        return (
          <button
            key={it.key}
            data-testid={`sidenav-${it.key}`}
            onClick={() => handle(it)}
            className={`group flex items-center gap-3.5 rounded-full transition-colors h-12 px-4 text-[15px] font-semibold ${
              activeItem
                ? "bg-white/10 text-[#FFA500]"
                : "text-white/75 hover:text-white hover:bg-white/5"
            }`}
          >
            <it.icon
              className="shrink-0 w-5 h-5 text-[#FFA500]"
              strokeWidth={2.2}
            />
            <span className="truncate">{it.label}</span>
          </button>
        );
      })}
    </nav>
  );

  const BottomControls = () => (
    <div className="mt-auto px-4 pb-6 pt-4 space-y-3">
      {helpOpen && (
        <div data-testid="sidenav-help-panel" className="rounded-2xl bg-white/5 border border-white/10 p-1">
          {secondaryItems.map((it) => (
            <button
              key={it.key}
              data-testid={`sidenav-help-${it.key}`}
              onClick={() => { setHelpOpen(false); setOpen(false); it.run(); }}
              className="w-full flex items-center gap-2.5 px-3 h-10 rounded-xl text-sm text-white/85 hover:text-white hover:bg-white/10 transition-colors text-left"
            >
              <it.icon className="w-4 h-4 text-[#FFA500] shrink-0" /> <span className="truncate">{it.label}</span>
            </button>
          ))}
        </div>
      )}
      {langOpen && (
        <div data-testid="sidenav-lang-panel" className="rounded-2xl bg-white/5 border border-white/10 p-1">
          {_langs.map((l) => (
            <button
              key={l}
              data-testid={`lang-option-${l}`}
              onClick={() => { i18n.changeLanguage(l); setLangOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 h-10 rounded-xl text-sm transition-colors text-left ${_cur === l ? "text-white bg-white/10" : "text-white/85 hover:text-white hover:bg-white/10"}`}
            >
              <Globe className="w-4 h-4 text-[#FFA500] shrink-0" /> <span className="truncate">{LANG_LABELS[l] || l.toUpperCase()}</span>
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        {isAdmin && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="country-selector"
              className="flex items-center gap-1.5 h-10 px-3 rounded-xl bg-white/5 border border-white/10 hover:border-white/25 transition-colors text-sm font-medium text-white"
            >
              <span className="text-base">{currentCountry?.flag || "🌍"}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="z-[1300]">
            {countries.map((c) => (
              <DropdownMenuItem key={c.code} data-testid={`country-option-${c.code}`} onClick={() => setCountry(c.code)}>
                <span className="mr-2">{c.flag}</span> {c.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        )}
        <button
          data-testid="lang-toggle"
          onClick={() => { setLangOpen((v) => !v); setHelpOpen(false); }}
          className="flex-1 flex items-center justify-center gap-1.5 h-10 px-3 rounded-xl bg-white/5 border border-white/10 hover:border-white/25 transition-colors text-sm font-semibold text-white"
        >
          <Globe className="w-4 h-4" /> {lang}
        </button>
        <button
          data-testid="sidenav-help"
          onClick={() => setHelpOpen((v) => !v)}
          className="flex-1 flex items-center justify-center gap-1.5 h-10 px-3 rounded-xl bg-white/5 border border-white/10 hover:border-white/25 transition-colors text-sm font-semibold text-white"
        >
          <LifeBuoy className="w-4 h-4" /> Aide
        </button>
      </div>
      <button
        data-testid="header-login-btn"
        onClick={() => { setOpen(false); navigate("/auth?role=passenger"); }}
        className="w-full h-12 rounded-full bg-[#F26419] hover:bg-[#D85312] text-white font-bold flex items-center justify-center gap-2 transition-colors"
      >
        <LogIn className="w-4 h-4" /> {t("nav.login")}
      </button>
    </div>
  );

  return (
    <>
      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-72 bg-[#1A1A1A] border-r border-white/5 z-40">
        <div className="px-6 py-6">
          <Logo onClick={() => scrollTo("top")} />
        </div>
        <div className="px-6 pb-3"><AdminZoneSwitch /></div>
        <NavList />
        <BottomControls />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-50 bg-[#1A1A1A] h-14 px-4 flex items-center relative">
        <button
          data-testid="sidenav-toggle"
          onClick={() => setOpen(true)}
          className="absolute left-4 w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center text-white"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="mx-auto">
          <Logo onClick={() => scrollTo("top")} />
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-[1200]" data-testid="sidenav-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[80vw] bg-[#1A1A1A] flex flex-col animate-in slide-in-from-left duration-200">
            <div className="px-5 py-5 flex items-center justify-between gap-2">
              <Logo onClick={() => { setOpen(false); scrollTo("top"); }} />
              <button data-testid="sidenav-close" onClick={() => setOpen(false)} className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-3 pb-2"><AdminZoneSwitch /></div>
            <NavList />
            <BottomControls />
          </div>
        </div>
      )}
    </>
  );
}
